import { AlertTriangle, CreditCard, QrCode } from 'lucide-react';

import AceiteTermosCondicoes from '@/components/legal/AceiteTermosCondicoes';

import { ANEL_OFF, ERRO, INPUT, LABEL, TITULO } from './checkoutUi';
import TermosAssinatura from './TermosAssinatura';

const ANEL_ON = 'inset 0 0 0 1.5px #ff2368';

const CAMPOS_CARTAO = [
  {
    id: 'numero',
    erro: 'cartaoNumero',
    label: 'Número do cartão',
    placeholder: '0000 0000 0000 0000',
    inputMode: 'numeric',
    autoComplete: 'cc-number',
    largo: true,
  },
  {
    id: 'nome',
    erro: 'cartaoNome',
    label: 'Nome impresso',
    placeholder: 'MARIA S SILVA',
    autoComplete: 'cc-name',
    largo: true,
  },
  {
    id: 'validade',
    erro: 'cartaoValidade',
    label: 'Validade',
    placeholder: 'MM/AA',
    inputMode: 'numeric',
    autoComplete: 'cc-exp',
  },
  {
    id: 'cvv',
    erro: 'cartaoCvv',
    label: 'CVC / CVV',
    placeholder: '123',
    inputMode: 'numeric',
    autoComplete: 'cc-csc',
  },
];

function Metodo({ ativo, onClick, Icone, nome, curto, nota }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className="flex cursor-pointer items-center gap-2.5 rounded-[14px] border-0 bg-white p-3 text-left lg:gap-3 lg:px-4 lg:py-3.5"
      style={{ boxShadow: ativo ? ANEL_ON : ANEL_OFF }}
    >
      <span
        className={`grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg lg:h-9 lg:w-9 lg:rounded-[10px] ${ativo ? 'bg-[#ff2368] text-white' : 'bg-[#f6f8fb] text-[#8a94a8]'}`}
      >
        <Icone size={16} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-xs font-bold uppercase text-[#0f1729] lg:text-[13px]">
          <span className="lg:hidden">{curto}</span>
          <span className="hidden lg:inline">{nome}</span>
        </span>
        <span className="hidden text-[11px] text-[#8a94a8] lg:block">
          {nota}
        </span>
      </span>
    </button>
  );
}

export default function PagamentoStep({
  assinatura,
  somentePix,
  metodoPagamento,
  setMetodoPagamento,
  dadosCartao,
  handleInputChange,
  erros,
  termoAceito,
  setTermoAceito,
  termosCondicoesAceitos,
  setTermosCondicoesAceitos,
}) {
  const nota = assinatura
    ? 'Renovação manual mensal'
    : 'Aprovação e liberação imediata';

  return (
    <div className="flex flex-col gap-3 lg:gap-3.5">
      <h2 className={TITULO}>Forma de pagamento</h2>

      {assinatura && (
        <div className="flex gap-2.5 rounded-[14px] border border-amber-100 bg-amber-50 p-3.5 text-xs leading-relaxed text-amber-900">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <p className="m-0">
            <strong>Renovação mensal manual.</strong>{' '}
            {metodoPagamento === 'cartao'
              ? 'O cartão é cobrado uma única vez agora, e não fica salvo para cobranças futuras.'
              : 'O PIX garante sua vaga nesta edição.'}{' '}
            A cada mês, nossa equipe entrará em contato para renovar seu acesso.
          </p>
        </div>
      )}

      <div
        className={`grid gap-2.5 lg:gap-3 ${somentePix ? 'grid-cols-1' : 'grid-cols-2'}`}
      >
        <Metodo
          ativo={metodoPagamento === 'pix'}
          onClick={() => setMetodoPagamento('pix')}
          Icone={QrCode}
          nome="Pix"
          curto="Pix"
          nota={nota}
        />
        {!somentePix && (
          <Metodo
            ativo={metodoPagamento === 'cartao'}
            onClick={() => setMetodoPagamento('cartao')}
            Icone={CreditCard}
            nome="Cartão de crédito"
            curto="Cartão"
            nota={nota}
          />
        )}
      </div>

      {metodoPagamento === 'cartao' && (
        <div className="grid grid-cols-2 gap-3 lg:gap-x-4 lg:gap-y-3.5">
          {CAMPOS_CARTAO.map((c) => (
            <label
              key={c.id}
              className={`flex flex-col gap-1.5 ${c.largo ? 'col-span-2 lg:col-span-1' : ''}`}
            >
              <span className={LABEL}>{c.label}</span>
              <input
                type="text"
                value={dadosCartao[c.id] || ''}
                onChange={(e) =>
                  handleInputChange(c.id, e.target.value, 'cartao')
                }
                placeholder={c.placeholder}
                inputMode={c.inputMode}
                autoComplete={c.autoComplete}
                className={INPUT}
              />
              {erros?.[c.erro] && <span className={ERRO}>{erros[c.erro]}</span>}
            </label>
          ))}
        </div>
      )}

      {assinatura && (
        <TermosAssinatura aceito={termoAceito} onChange={setTermoAceito} />
      )}

      <AceiteTermosCondicoes
        simples
        aceito={termosCondicoesAceitos}
        onChange={setTermosCondicoesAceitos}
      />

      {erros?.global && (
        <div
          className="flex items-start gap-2.5 rounded-[14px] border border-red-100 bg-red-50 p-3.5"
          role="alert"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-500" />
          <div>
            <p className="m-0 text-xs font-extrabold uppercase text-red-600">
              Não foi possível concluir
            </p>
            <p className="m-0 mt-1 text-xs leading-relaxed text-red-600">
              {erros.global}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
